"use strict";
// Roles, scopes and the permissions matrix (plan sections 2 and 4.10).
// This file is the single source used by the seed, the API and the admin UI.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLE_BY_CODE = exports.ROLES = exports.PERMISSIONS = exports.PERMISSION_CODES = exports.ROLE_CODES = exports.SCOPE_TYPES = void 0;
exports.SCOPE_TYPES = ['GLOBAL', 'HUB', 'TEAM', 'PROJECT'];
exports.ROLE_CODES = [
    'super_admin',
    'ops_manager',
    'hub_manager',
    'team_leader',
    'dispatcher',
    'cashier',
    'finance',
    'auditor',
    'project_viewer',
    'courier',
];
exports.PERMISSION_CODES = [
    'users.read',
    'users.manage',
    'roles.read',
    'activity.read',
    'org.read',
    'setup.read',
    'setup.manage',
    'couriers.manage',
    'consignees.read',
    'consignees.manage',
    'rates.read',
    'rates.manage',
    'intake.read',
    'intake.upload',
    'hub.receive',
    'addresses.fix',
    'messages.read',
    'shipments.read',
    'shipments.manage',
    'shipments.assign',
    'edits.request',
    'edits.approve',
    'exports.run',
    'labels.print',
    'courier.app',
    'evidence.sensitive',
    'manual_awb.approve',
    'cash.read',
    'cash.receive',
    'route_start.override',
    'dispatch.map',
];
exports.PERMISSIONS = [
    { code: 'users.read', group: 'users', description: 'View users and their roles' },
    { code: 'users.manage', group: 'users', description: 'Create and edit users, assign roles, reset passwords' },
    { code: 'roles.read', group: 'users', description: 'View roles and the permissions matrix' },
    { code: 'activity.read', group: 'audit', description: 'View the activity log' },
    { code: 'org.read', group: 'org', description: 'View hubs, projects and teams' },
    { code: 'setup.read', group: 'setup', description: 'View setup: projects, merchants, hubs, teams, couriers' },
    { code: 'setup.manage', group: 'setup', description: 'Change setup: projects, merchants, hubs, teams' },
    { code: 'couriers.manage', group: 'setup', description: 'Add couriers, edit profiles, move couriers between teams' },
    { code: 'consignees.read', group: 'consignees', description: 'Search customers by mobile and open their saved places (opening is logged)' },
    { code: 'consignees.manage', group: 'consignees', description: 'Remove a wrong saved place from a customer' },
    { code: 'rates.read', group: 'finance', description: 'View rate cards' },
    { code: 'rates.manage', group: 'finance', description: 'Create rate cards and change future ones (values from Finance)' },
    { code: 'intake.read', group: 'intake', description: 'View uploaded client files and their rows' },
    { code: 'intake.upload', group: 'intake', description: 'Upload client files, match columns and import shipments' },
    { code: 'hub.receive', group: 'hub', description: 'Record hub arrivals and scan pieces in at the hub' },
    { code: 'addresses.fix', group: 'shipments', description: 'Fix shipment addresses: set the pin or district, ask the customer for their location on WhatsApp' },
    { code: 'messages.read', group: 'messaging', description: 'View the WhatsApp messages sent to customers' },
    { code: 'shipments.read', group: 'shipments', description: 'View shipments, their tasks and timeline (customer data masked)' },
    { code: 'shipments.manage', group: 'shipments', description: 'Change shipment status as allowed for the role (hold, reschedule, RTO, transfer…) and edit before dispatch' },
    { code: 'shipments.assign', group: 'shipments', description: 'Give shipments to a courier and take them back' },
    { code: 'edits.request', group: 'shipments', description: 'Ask for a change after dispatch; team leaders apply urgent address, pin and alternate-phone fixes' },
    { code: 'edits.approve', group: 'shipments', description: 'Approve or reject changes after dispatch (hub manager only after 30 minutes)' },
    { code: 'exports.run', group: 'shipments', description: 'Export shipment lists to Excel (logged)' },
    { code: 'labels.print', group: 'shipments', description: 'Print AWB labels and hub-transfer / RTO manifests' },
    { code: 'courier.app', group: 'courier', description: 'Use the courier (driver) app for own tasks' },
    { code: 'evidence.sensitive', group: 'audit', description: 'See Emirates ID photos (proof of delivery); every view is logged' },
    { code: 'manual_awb.approve', group: 'shipments', description: "Approve or reject a courier's manual AWB request (damaged label), with the photos" },
    { code: 'cash.read', group: 'cash', description: "See couriers' cash in hand, handovers and receipts" },
    { code: 'cash.receive', group: 'cash', description: 'Receive cash from couriers by scanning their QR; gives the digital receipt' },
    { code: 'route_start.override', group: 'cash', description: 'Let a courier start the route although earlier cash is not handed over, with a reason' },
    { code: 'dispatch.map', group: 'planning', description: 'Map couriers to dispatchers and team leaders for the day; give projects to team leaders and dispatchers in own hub' },
];
/**
 * Phase 0 matrix. Later phases add permissions (shipments, cash, audit…).
 * Scope limits what a permission covers: a hub manager with users.read
 * sees only users assigned inside their hub.
 */
exports.ROLES = [
    {
        code: 'super_admin',
        name: 'Super admin',
        scopeType: 'GLOBAL',
        description: 'Sees and does everything (D-0016)',
        permissions: [...exports.PERMISSION_CODES],
    },
    {
        code: 'ops_manager',
        name: 'Ops manager',
        scopeType: 'GLOBAL',
        description: 'Runs all projects; approves edits, shortages, deductions; marks settlements Paid',
        permissions: ['users.read', 'roles.read', 'activity.read', 'org.read', 'setup.read', 'setup.manage', 'couriers.manage', 'consignees.read', 'consignees.manage', 'rates.read', 'intake.read', 'intake.upload', 'hub.receive', 'addresses.fix', 'messages.read', 'shipments.read', 'shipments.manage', 'edits.request', 'edits.approve', 'exports.run', 'labels.print', 'evidence.sensitive', 'cash.read', 'dispatch.map'],
    },
    {
        code: 'hub_manager',
        name: 'Hub manager',
        scopeType: 'HUB',
        description: 'Uploads, receipt, dispatch and cash at one hub',
        permissions: ['users.read', 'activity.read', 'org.read', 'setup.read', 'consignees.read', 'consignees.manage', 'intake.read', 'intake.upload', 'hub.receive', 'addresses.fix', 'messages.read', 'shipments.read', 'shipments.manage', 'shipments.assign', 'edits.request', 'edits.approve', 'exports.run', 'labels.print', 'evidence.sensitive', 'manual_awb.approve', 'cash.read', 'cash.receive', 'route_start.override', 'dispatch.map'],
    },
    {
        code: 'team_leader',
        name: 'Team leader',
        scopeType: 'HUB',
        projectInHub: true,
        description: 'Leads the projects given to them in a hub: maps couriers to dispatchers, decides failed parcels, closes the day (D-0020)',
        permissions: ['users.read', 'org.read', 'setup.read', 'consignees.read', 'intake.read', 'shipments.read', 'shipments.manage', 'edits.request', 'exports.run', 'manual_awb.approve', 'dispatch.map'],
    },
    {
        code: 'dispatcher',
        name: 'Dispatcher',
        scopeType: 'HUB',
        projectInHub: true,
        description: 'Sorts, assigns, asks for locations, plans routes',
        permissions: ['org.read', 'setup.read', 'consignees.read', 'intake.read', 'intake.upload', 'hub.receive', 'addresses.fix', 'messages.read', 'shipments.read', 'shipments.manage', 'shipments.assign', 'exports.run', 'labels.print'],
    },
    {
        code: 'cashier',
        name: 'Cashier',
        scopeType: 'HUB',
        description: 'Receives courier cash, records shortages, prepares COD polybags',
        permissions: ['cash.read', 'cash.receive'],
    },
    {
        code: 'finance',
        name: 'Finance',
        scopeType: 'GLOBAL',
        description: 'Rate cards, deductions ledger, payroll export, invoices',
        permissions: ['setup.read', 'rates.read', 'rates.manage', 'cash.read'],
    },
    {
        code: 'auditor',
        name: 'Auditor',
        scopeType: 'GLOBAL',
        description: 'Audits proof of attempt and proof of delivery',
        permissions: ['users.read', 'activity.read', 'setup.read', 'consignees.read', 'intake.read', 'messages.read', 'shipments.read', 'evidence.sensitive', 'cash.read'],
    },
    {
        code: 'project_viewer',
        name: 'Project viewer',
        scopeType: 'PROJECT',
        description: "Client staff: sees only their own project's data",
        permissions: ['shipments.read', 'exports.run'],
    },
    {
        code: 'courier',
        name: 'Courier',
        /** the courier's home hub (D-0020) */
        scopeType: 'HUB',
        description: 'Scans, delivers, calls, records failures, hands over cash (own tasks only)',
        permissions: ['courier.app'],
    },
];
exports.ROLE_BY_CODE = Object.fromEntries(exports.ROLES.map((r) => [r.code, r]));
//# sourceMappingURL=roles.js.map